import { format } from 'date-fns';
import { useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { TrlFormDropdown, TrlTextField } from '»components/atoms/Inputs';
import { TrlDatePicker } from '»components/atoms/Inputs/TrlDatePicker';
import {
  getAvailableOrdTypes,
  getAvailablePriceTypes,
  operations,
} from '»components/templates/TrlInsertOrder/TrlInsertOrderModal/TrlInsertOrderModal.helper.selectors';
import { useCurrentPage, useDotsNotCommas } from '»utils/customHooks';

export const TrlStockExchangeFields = ({
  orderPossibilities,
  instrumentInfo,
}) => {
  const [availableOrdTypes, setAvailableOrdTypes] = useState(
    getAvailableOrdTypes(orderPossibilities),
  );
  const [availablePriceTypes, setAvailablePriceTypes] = useState(
    getAvailablePriceTypes(orderPossibilities),
  );

  const { values, setFieldValue, validateForm } = useFormikContext();

  const { replaceCommasWithDots } = useDotsNotCommas();
  const { isMarketStockExchangeOrders } = useCurrentPage();

  const { orderMaxDays } = instrumentInfo;

  const {
    selectOrderType,
    selectOperation,
    inputQuantity,
    selectPriceType,
    inputPrice,
    selectExpirationDate,
    inputStopPrice,
  } = values;

  const stockExchangeOperations = operations.filter(
    (operation) => operation.market === 'stockExchange',
  );

  useEffect(() => {
    setAvailableOrdTypes(getAvailableOrdTypes(orderPossibilities));
    setAvailablePriceTypes(getAvailablePriceTypes(orderPossibilities));
  }, [orderPossibilities]);

  return (
    <>
      <TrlFormDropdown
        name="selectOrderType"
        label={<FormattedMessage id="TRLT_UI-ORDER_TYPE" />}
        value={selectOrderType}
        options={availableOrdTypes.filter((ordType) => ordType.show)}
        onChangeValue={(e) =>
          getAvailablePriceTypes(
            orderPossibilities,
            e.target.value,
            setAvailablePriceTypes,
          )
        }
        disabled={isMarketStockExchangeOrders}
      />
      <TrlFormDropdown
        name="selectOperation"
        label={<FormattedMessage id="TRLT_UI-OPERATION" />}
        value={selectOperation}
        options={stockExchangeOperations}
        disabled={isMarketStockExchangeOrders}
      />
      <TrlTextField
        name="inputQuantity"
        type="number"
        label={<FormattedMessage id="TRLT_UI-QUANTITY" />}
        value={inputQuantity}
        additionalFunc={(event) => {
          replaceCommasWithDots({
            inputName: 'inputQuantity',
            inputValue: event.target.value,
          });
        }}
        hasPadding
      />
      <TrlFormDropdown
        name="selectPriceType"
        label={<FormattedMessage id="TRLT_UI-PRICE_TYPE" />}
        value={selectPriceType}
        options={availablePriceTypes.filter((priceType) => priceType.show)}
        onChangeValue={async (event) => {
          getAvailableOrdTypes(
            orderPossibilities,
            event.target.value,
            setAvailableOrdTypes,
          );

          if (event.target.value === 1) {
            await setFieldValue(
              'selectExpirationDate',
              format(new Date(), "yyyy-MM-dd'T'00:00:00'Z'"),
              false,
            );
            validateForm();
          }
        }}
        disabled={isMarketStockExchangeOrders}
      />
      <TrlTextField
        name="inputPrice"
        type="number"
        label={<FormattedMessage id="TRLT_UI-PRICE" />}
        value={selectPriceType === 1 ? '' : inputPrice}
        disabled={selectPriceType === 1}
        additionalFunc={(event) => {
          replaceCommasWithDots({
            inputName: 'inputPrice',
            inputValue: event.target.value,
          });
        }}
        hasPadding
      />
      <TrlDatePicker
        name="selectExpirationDate"
        label={<FormattedMessage id="TRLT_UI-EXPIRATION_DATE" />}
        value={selectExpirationDate}
        isDisabled={selectPriceType === 1}
        disablePast
        disableWeekends
        maxBusinessDays={orderMaxDays}
        dateFormatType="dateTime"
      />
      <TrlTextField
        name="inputStopPrice"
        type="number"
        label={<FormattedMessage id="TRLT_UI-PRICE_STOP" />}
        value={selectOrderType === 1 ? '' : inputStopPrice}
        disabled={selectOrderType === 1}
        additionalFunc={(event) => {
          replaceCommasWithDots({
            inputName: 'inputStopPrice',
            inputValue: event.target.value,
          });
        }}
        hasPadding
      />
    </>
  );
};

TrlStockExchangeFields.propTypes = {
  orderPossibilities: PropTypes.array.isRequired,
  instrumentInfo: PropTypes.object.isRequired,
};
